/**
 * Manager de miembros de proyecto: lista, agregar (multi-select con
 * autocomplete) y remover. El endpoint /api/users está restringido a
 * admin/lead — el componente lo intenta y si recibe 403, oculta el
 * botón "Agregar".
 */
import { useMemo, useState, forwardRef, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X, Search, Check } from "lucide-react";
import clsx from "clsx";
import { api, ApiError } from "@/lib/api";

interface Member {
  userId: string;
  roleInProject: "lead" | "collaborator" | "observer";
  name: string;
  email: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  active: boolean;
}

const ROLES = [
  { value: "lead", label: "Lead" },
  { value: "collaborator", label: "Colaborador" },
  { value: "observer", label: "Observador" },
] as const;

export function MembersManager({ projectId, members }: { projectId: string; members: Member[] }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const qc = useQueryClient();

  // Probamos el endpoint; si 403, escondemos el botón de agregar.
  const usersQuery = useQuery<{ users: User[] }>({
    queryKey: ["users"],
    queryFn: () => api.get("/users"),
    retry: false,
    staleTime: 5 * 60_000,
  });

  const canManage =
    !usersQuery.error || !(usersQuery.error instanceof ApiError && usersQuery.error.status === 403);

  const remove = useMutation({
    mutationFn: (userId: string) => api.del(`/projects/${projectId}/members/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectId] }),
  });

  return (
    <>
      <div className="space-y-2.5">
        {members.length === 0 ? (
          <p className="text-sm text-base-content/55 italic">Sin miembros asignados aún.</p>
        ) : (
          <ul className="space-y-2.5 -mx-1">
            {members.map((m) => (
              <li
                key={m.userId}
                className="px-1 py-1 rounded-md hover:bg-base-200/60 group flex items-center justify-between gap-2 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{m.name}</div>
                  <div className="text-xs text-base-content/55 truncate">{m.email}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="badge badge-ghost badge-sm capitalize">{m.roleInProject}</span>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => remove.mutate(m.userId)}
                      disabled={remove.isPending && remove.variables === m.userId}
                      className="btn btn-ghost btn-xs btn-square opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label={`Quitar ${m.name}`}
                    >
                      <X className="size-3.5 text-error" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {canManage && (
          <button
            type="button"
            onClick={() => dialogRef.current?.showModal()}
            className="btn btn-ghost btn-sm w-full justify-start text-base-content/70 hover:text-primary"
          >
            <Plus className="size-3.5" /> Agregar miembros
          </button>
        )}
      </div>

      <AddMemberDialog
        ref={dialogRef}
        projectId={projectId}
        existingIds={new Set(members.map((m) => m.userId))}
        users={usersQuery.data?.users ?? []}
        onClose={() => dialogRef.current?.close()}
      />
    </>
  );
}

// ─── Dialog ───────────────────────────────────────────────────────────────

interface AddMemberDialogProps {
  projectId: string;
  existingIds: Set<string>;
  users: User[];
  onClose: () => void;
}

const AddMemberDialog = forwardRef<HTMLDialogElement, AddMemberDialogProps>(
  function AddMemberDialog({ projectId, existingIds, users, onClose }, ref) {
    const qc = useQueryClient();
    const [query, setQuery] = useState("");
    // Multi-select: Set de ids para que toggle sea barato. Map adicional
    // para tener el User completo de los seleccionados (sirve para los
    // chips de "ya seleccionados" arriba del listado).
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [role, setRole] = useState<(typeof ROLES)[number]["value"]>("collaborator");

    const usersById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
    const selectedUsers = useMemo(
      () => [...selectedIds].map((id) => usersById.get(id)).filter((u): u is User => !!u),
      [selectedIds, usersById],
    );

    const matches = useMemo(() => {
      const q = query.trim().toLowerCase();
      // Filtramos no-activos y ya-miembros del proyecto, pero los
      // seleccionados SIGUEN apareciendo en la lista (con su check)
      // para que el usuario pueda deseleccionar sin perderlos de vista.
      const available = users.filter((u) => u.active && !existingIds.has(u.id));
      if (!q) return available.slice(0, 12);
      return available
        .filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
        .slice(0, 12);
    }, [users, existingIds, query]);

    const toggle = (id: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    };

    const add = useMutation({
      // Hacemos N POSTs en paralelo. Es N pequeño (rara vez >20) y cada
      // uno es independiente (idempotent en backend con UNIQUE(project,user)).
      // Si alguno falla, mostramos error pero los exitosos quedan agregados.
      mutationFn: async () => {
        await Promise.all(
          [...selectedIds].map((userId) =>
            api.post(`/projects/${projectId}/members`, {
              userId,
              roleInProject: role,
            }),
          ),
        );
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["project", projectId] });
        reset();
        onClose();
      },
    });

    const reset = () => {
      setQuery("");
      setSelectedIds(new Set());
      setRole("collaborator");
    };

    const count = selectedIds.size;

    return (
      <dialog ref={ref} className="modal" onClose={reset}>
        <div className="modal-box card-elevated max-w-lg">
          <h3 className="text-lg font-display font-semibold mb-3">
            Agregar miembros
            {count > 0 && (
              <span className="text-sm font-normal text-base-content/55 ml-2">
                · {count} seleccionado{count === 1 ? "" : "s"}
              </span>
            )}
          </h3>

          {/* Chips de seleccionados — visibles arriba para que el usuario
              vea quién va a entrar sin tener que scrollear la lista. */}
          {selectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3 pb-3 border-b border-base-300/60">
              {selectedUsers.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggle(u.id)}
                  className="badge badge-primary gap-1.5 cursor-pointer hover:badge-error transition-colors"
                  title="Quitar de la selección"
                >
                  {u.name}
                  <X className="size-3" />
                </button>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <label className="label py-1">
              <span className="label-text text-sm font-medium">Buscar usuario</span>
            </label>
            <div className="relative">
              <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
              <input
                className="input input-bordered w-full pl-9"
                placeholder="Nombre o email"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
            </div>
            <ul className="max-h-64 overflow-y-auto -mx-2 mt-2">
              {matches.length === 0 ? (
                <li className="px-2 py-3 text-sm text-base-content/55 italic">
                  {query ? "Sin resultados." : "No hay usuarios disponibles."}
                </li>
              ) : (
                matches.map((u) => {
                  const isSelected = selectedIds.has(u.id);
                  return (
                    <li key={u.id}>
                      <button
                        type="button"
                        onClick={() => toggle(u.id)}
                        className={clsx(
                          "w-full px-2 py-2 text-left rounded-md transition-colors flex items-center gap-3",
                          isSelected ? "bg-primary/10" : "hover:bg-base-200",
                        )}
                      >
                        <span
                          className={clsx(
                            "size-5 rounded grid place-items-center border shrink-0 transition-colors",
                            isSelected
                              ? "bg-primary border-primary text-primary-content"
                              : "border-base-300",
                          )}
                          aria-hidden
                        >
                          {isSelected && <Check className="size-3.5" />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div
                            className={clsx(
                              "text-sm truncate",
                              isSelected ? "font-semibold text-primary" : "font-medium",
                            )}
                          >
                            {u.name}
                          </div>
                          <div className="text-xs text-base-content/55 truncate">{u.email}</div>
                        </div>
                        <span className="badge badge-ghost badge-sm capitalize shrink-0">
                          {u.role}
                        </span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>

          {count > 0 && (
            <div className="mt-3 pt-3 border-t border-base-300/60">
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Rol en el proyecto</span>
                <span className="label-text-alt text-base-content/55">aplica a los {count}</span>
              </label>
              <div className="flex flex-wrap gap-1.5">
                {ROLES.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setRole(r.value)}
                    className={clsx("btn btn-sm", role === r.value ? "btn-primary" : "btn-ghost")}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 mt-2 border-t border-base-300/60">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                reset();
                onClose();
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => add.mutate()}
              disabled={count === 0 || add.isPending}
            >
              {add.isPending && <span className="loading loading-spinner loading-xs" />}
              Agregar {count > 0 ? `(${count}) ` : ""}al proyecto
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>cerrar</button>
        </form>
      </dialog>
    );
  },
);
